import {
  API_URL,
  v,
  put,
  debug,
  request,
  takeEvery,
  createDoRequestAction,
  assertTotalSchema,
  SagaIterator,
  call,
} from "../../lib";
import { clearLogoutPending } from "../../lib/auth/logout_pending";
import { withLoginClaim } from "../../lib/auth/pending_logout_retry";
import { startDeadlineRequest, startSignedInSession } from "../../lib/auth/session_deadlines";
import { toProofSafeSchemaError } from "../../model/auth_proof";

import {
  doAUTH_SET,
  doSETTINGS_SET,
  doREQUEST_ERROR,
  doREQUEST_COMPLETE,
  doPROJECT_SETTINGS_LIST_SET,
  doFAVORITE_LIST_SET,
  doIDENTITY_SET,
} from "../state";

export const EMAIL_VERIFY_SUBMIT = "EMAIL_VERIFY_SUBMIT";
export const doEMAIL_VERIFY_SUBMIT = createDoRequestAction(EMAIL_VERIFY_SUBMIT);

export const emailVerifySubmitActionCreators = {
  doEMAIL_VERIFY_SUBMIT,
};

export function* emailVerifySubmit({ payload, setRequestResult }: any): SagaIterator {
  debug("saga*emailVerifySubmit");

  try {
    assertTotalSchema({
      payload,
      schema: v.object({
        requestId: v.string().uuid().exist(),
        email: v.string().exist(),
        verificationCode: v.string().exist(),
      }),
    });
  } catch (error) {
    // the error reduced to `isJoi` and a message: the ValidationError itself quotes the rejected
    // payload, verification code included (see `toProofSafeSchemaError`)
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: EMAIL_VERIFY_SUBMIT,
          requestId: payload?.requestId,
          error: toProofSafeSchemaError(error),
        },
        setRequestResult,
      ),
    );
  }

  const { verificationCode, email } = payload;

  // holds the login claim and waits out a logout retry in flight, so neither a retry already out
  // nor one started while this request is on the wire can clear its cookie; see `withLoginClaim`
  return yield* withLoginClaim(function* () {
    const deadlineRequest = startDeadlineRequest();
    const result = yield request({
      method: "post",
      url: `${API_URL}/v1/web/identity-email-verify-submit`,
      data: {
        email,
        verificationCode,
      },
    });

    if (!result.success || result.error) {
      // never the raw error, which carries the request body (verification code included) on `config.data`,
      // and no `fromPayload`; see "Credential-Carrying Sagas" in src/store/saga/AGENTS.md
      debug("saga*emailVerifySubmit failed", { statusCode: result.statusCode, type: result.type });
      return yield put(
        doREQUEST_ERROR(
          {
            fromType: EMAIL_VERIFY_SUBMIT,
            requestId: payload.requestId,
            url: "/v1/web/identity-email-verify-submit",
            statusCode: result.statusCode,
            type: result.type,
            payload: result.payload,
            headers: result.headers,
          },
          setRequestResult,
        ),
      );
    }

    const {
      settings,
      projectSettingsList,
      workspaceFavoriteList,
      accessToken,
      accessTokenExpiresInSeconds,
      refreshTokenExpiresInSeconds,
      identity,
    } = result.payload;

    yield put(
      doAUTH_SET({
        accessToken,
        accessTokenExpiresInSeconds,
        refreshTokenExpiresInSeconds,
        ...startSignedInSession(result.payload, deadlineRequest),
      }),
    );

    // a new session retires the old one's marker, as a login does; left in place, the next retry
    // would end this session on the server
    yield call(clearLogoutPending);

    yield put(doSETTINGS_SET(settings));

    yield put(doPROJECT_SETTINGS_LIST_SET(projectSettingsList));

    yield put(doFAVORITE_LIST_SET(workspaceFavoriteList));

    yield put(doIDENTITY_SET(identity));

    yield put(
      doREQUEST_COMPLETE(
        {
          fromType: EMAIL_VERIFY_SUBMIT,
          requestId: payload.requestId,
          ...result,
        },
        setRequestResult,
      ),
    );
  });
}

export function* emailVerifySubmitSaga() {
  yield takeEvery(EMAIL_VERIFY_SUBMIT, emailVerifySubmit);
}
